// 특수액션 시스템 정의 및 관리

export enum ActionTargetType {
  PLAYER = "player",
  MULTI_PLAYER = "multi_player", // 다중 플레이어 선택 (nominate 등)
  CHOICE = "choice",
  SELF = "self",
  ALL = "all",
  ROLE = "role", // 역할 선택 (see_roles 등)
}

export enum ActionResultType {
  PUBLIC = "public", // 개별 행동이 모두 공개
  PRIVATE = "private", // 집계된 결과만 공개
}

export interface CoreAction {
  id: string;
  name: string;
  icon: string;
  description: string;
  targetType: ActionTargetType;
  resultType: ActionResultType;
}

export interface AbilityUsage {
  playerId: number;
  abilityId: string;
  targetId?: number;
  targetIds?: number[]; // 다중 플레이어 선택용 (nominate 등)
  choice?: boolean; // 찬반투표용
  selectedRole?: string; // 역할 선택용 (see_roles) - 단일 선택 (legacy)
  selectedRoles?: string[]; // 역할 선택용 (see_roles) - 멀티 선택
  timestamp: number;
  result?: AbilityResult;
}

export interface AbilityResult {
  type: "investigation" | "protection" | "assassination" | "vote" | "general";
  success: boolean;
  message: string;
  data?: any; // 액션별 구체적 결과 데이터
  isPublic: boolean; // 공개 여부
  targetPlayerId?: number; // 결과를 받을 대상 (조사 등)
}

export interface AbilityPhase {
  isActive: boolean;
  startedBy: number;
  startTime?: number;
  duration?: number; // 타이머 지속 시간 (초)
  endTime?: number; // 종료 예정 시간 (timestamp)
  pendingResults: AbilityUsage[]; // 펜딩 중인 액션 사용 결과들
}

// 기본 액션 정의 (아이콘과 메타데이터)
export const ACTION_METADATA = [
  {
    id: "assassinate",
    icon: "🗡️",
    targetType: ActionTargetType.PLAYER,
    resultType: ActionResultType.PUBLIC,
  },
  {
    id: "protect",
    icon: "🛡️",
    targetType: ActionTargetType.PLAYER,
    resultType: ActionResultType.PUBLIC,
  },
  {
    id: "investigate",
    icon: "🔍",
    targetType: ActionTargetType.PLAYER,
    resultType: ActionResultType.PUBLIC,
  },
  {
    id: "nominate",
    icon: "👉",
    targetType: ActionTargetType.MULTI_PLAYER,
    resultType: ActionResultType.PUBLIC,
  },
  {
    id: "vote",
    icon: "🗳️",
    targetType: ActionTargetType.PLAYER,
    resultType: ActionResultType.PRIVATE,
  },
  {
    id: "vote_choice",
    icon: "👍",
    targetType: ActionTargetType.CHOICE,
    resultType: ActionResultType.PUBLIC,
  },
  {
    id: "secret_vote_choice",
    icon: "🔒",
    targetType: ActionTargetType.CHOICE,
    resultType: ActionResultType.PRIVATE,
  },
  {
    id: "see_roles",
    icon: "👁️",
    targetType: ActionTargetType.ROLE,
    resultType: ActionResultType.PRIVATE,
  },
];

// localized 액션 목록 생성
export const getCoreActions = (t: any): CoreAction[] => {
  return ACTION_METADATA.map((meta) => ({
    ...meta,
    name: t(`RoleDistributor.${meta.id}`),
    description: t(`RoleDistributor.${meta.id}Desc`),
  }));
};

// localized 액션을 반환하는 함수로 대체 (하위 호환성 유지)
export const getCoreAbilitiesWithTranslation = (t: any): CoreAction[] =>
  getCoreActions(t);

// 하위 호환을 위한 fallback (localization 없이 사용되는 경우)
export const CORE_ABILITIES: CoreAction[] = ACTION_METADATA.map((meta) => ({
  ...meta,
  name: meta.id, // fallback으로 ID 사용
  description: `${meta.id}_desc`, // fallback으로 ID_desc 사용
}));

// 액션 ID로 액션 정보 조회
export const getActionById = (id: string): CoreAction | undefined => {
  return CORE_ABILITIES.find((ability) => ability.id === id);
};

// 플레이어가 보유한 액션 목록 조회
export const getPlayerActions = (playerActionIds: string[]): CoreAction[] => {
  return playerActionIds
    .map((id) => getActionById(id))
    .filter((action): action is CoreAction => action !== undefined);
};

// 액션 사용 가능 여부 확인
export const canUseAction = (
  actionId: string,
  playerId: number,
  actionPhase: AbilityPhase,
  recentUsages: AbilityUsage[]
): boolean => {
  // 특수액션 단계가 활성화되어야 함
  if (!actionPhase.isActive) {
    return false;
  }

  // 타이머가 있고 종료 시간이 지났으면 사용 불가
  if (actionPhase.endTime && Date.now() > actionPhase.endTime) {
    return false;
  }

  // 현재 활성화된 액션 페이즈에서만 사용 여부 확인
  // startTime이 있으면 그 이후의 사용만 체크
  const phaseStartTime = actionPhase.startTime || 0;
  const hasUsedInCurrentPhase = recentUsages.some(
    (usage) =>
      usage.playerId === playerId &&
      usage.abilityId === actionId &&
      usage.timestamp >= phaseStartTime
  );

  return !hasUsedInCurrentPhase;
};

// 현재 페이즈에서 해당 액션을 이미 사용했는지 확인
export const hasUsedActionInCurrentPhase = (
  actionId: string,
  playerId: number,
  actionPhase: AbilityPhase,
  recentUsages: AbilityUsage[]
): boolean => {
  if (!actionPhase.isActive) return false;

  const phaseStartTime = actionPhase.startTime || 0;
  return recentUsages.some(
    (usage) =>
      usage.playerId === playerId &&
      usage.abilityId === actionId &&
      usage.timestamp >= phaseStartTime
  );
};

// 현재 페이즈에서 플레이어의 특정 액션 사용 기록 조회
export const getPlayerActionInCurrentPhase = (
  actionId: string,
  playerId: number,
  actionPhase: AbilityPhase,
  recentUsages: AbilityUsage[]
): AbilityUsage | undefined => {
  if (!actionPhase.isActive) return undefined;

  const phaseStartTime = actionPhase.startTime || 0;
  return recentUsages.find(
    (usage) =>
      usage.playerId === playerId &&
      usage.abilityId === actionId &&
      usage.timestamp >= phaseStartTime
  );
};

// 액션 사용 결과 생성
export const createActionUsage = (
  playerId: number,
  actionId: string,
  targetId?: number,
  choice?: boolean
): AbilityUsage => {
  return {
    playerId,
    abilityId: actionId,
    targetId,
    choice,
    timestamp: Date.now(),
  };
};

// 암살-보호 상호작용 처리
export const processActionInteractions = (
  pendingUsages: AbilityUsage[],
  allPlayers: any[],
  t: any
): AbilityUsage[] => {
  const processedUsages: AbilityUsage[] = [];

  // 먼저 보호 액션들을 찾음
  const protections = pendingUsages.filter(
    (usage) => usage.abilityId === "protect"
  );
  const protectedPlayerIds = protections.map((p) => p.targetId).filter(Boolean);

  // 각 액션별로 결과 처리
  pendingUsages.forEach((usage) => {
    const processed = { ...usage };

    switch (usage.abilityId) {
      case "assassinate":
        // 암살 대상이 보호받고 있는지 확인
        const isProtected = protectedPlayerIds.includes(usage.targetId);
        if (isProtected) {
          processed.result = {
            type: "assassination",
            success: false,
            message: t("RoleDistributor.assassinationFailed"),
            isPublic: false, // 보호로 인한 암살 실패는 비공개
            targetPlayerId: usage.playerId,
          };
        } else {
          const targetPlayer = allPlayers.find((p) => p.id === usage.targetId);
          processed.result = {
            type: "assassination",
            success: true,
            message: t("RoleDistributor.assassinationSuccess", {
              playerName: targetPlayer?.name || "Target",
            }),
            data: {
              targetName: targetPlayer?.name,
              targetRole: targetPlayer?.label,
            },
            isPublic: true,
            targetPlayerId: usage.targetId,
          };
        }
        break;

      case "protect":
        // 보호는 기본적으로 결과를 공개하지 않음
        const protectedPlayer = allPlayers.find((p) => p.id === usage.targetId);
        processed.result = {
          type: "protection",
          success: true,
          message: t("RoleDistributor.protectionSuccess", {
            playerName: protectedPlayer?.name || "Player",
          }),
          isPublic: false,
          targetPlayerId: usage.playerId,
        };
        break;

      case "investigate":
        const investigatedPlayer = allPlayers.find(
          (p) => p.id === usage.targetId
        );
        processed.result = {
          type: "investigation",
          success: true,
          message: t("RoleDistributor.investigationResult", {
            playerName: investigatedPlayer?.name || "Player",
            role: investigatedPlayer?.label || "Unassigned",
          }),
          data: {
            targetName: investigatedPlayer?.name,
            targetRole: investigatedPlayer?.label || "Unassigned",
          },
          isPublic: false, // 조사 결과는 조사자에게만 공개
          targetPlayerId: usage.playerId, // 조사한 사람이 결과를 받음
        };
        break;

      case "nominate":
        // 공개 지목 (다중 선택 지원)
        const nominatingPlayer = allPlayers.find((p) => p.id === usage.playerId);

        // targetIds 또는 targetId 지원 (하위 호환성)
        const targetPlayerIds = usage.targetIds || (usage.targetId ? [usage.targetId] : []);
        const nominatedPlayers = targetPlayerIds
          .map((id) => allPlayers.find((p) => p.id === id))
          .filter(Boolean);
        const nominatedNames = nominatedPlayers.map((p) => p?.name || "Unknown").join(", ");

        processed.result = {
          type: "general",
          success: true,
          message: t("RoleDistributor.nominateSuccess", {
            playerName: nominatingPlayer?.name || "Player",
            targetName: nominatedNames || "Target",
          }),
          data: {
            targetIds: targetPlayerIds,
            targetNames: nominatedPlayers.map((p) => p?.name),
          },
          isPublic: true,
        };
        break;

      case "vote_choice":
        // 공개 찬반투표
        const votingPlayer = allPlayers.find((p) => p.id === usage.playerId);
        const choiceText = usage.choice
          ? t("RoleDistributor.agree")
          : t("RoleDistributor.disagree");
        processed.result = {
          type: "general",
          success: true,
          message: t("RoleDistributor.voteChoiceSuccess", {
            playerName: votingPlayer?.name || "Player",
            choice: choiceText,
          }),
          isPublic: true,
        };
        break;

      case "see_roles":
        // 역할 확인 (패시브 능력, 게임 시작 시 이미 표시되었으나 액션으로도 추적 가능)
        processed.result = {
          type: "general",
          success: true,
          message: t("RoleDistributor.seeRolesSuccess"),
          isPublic: false,
          targetPlayerId: usage.playerId,
        };
        break;

      default:
        processed.result = {
          type: "general",
          success: true,
          message: t("RoleDistributor.actionComplete", {
            actionName: getActionById(usage.abilityId)?.name || "Action",
          }),
          isPublic: true,
        };
    }

    processedUsages.push(processed);
  });

  return processedUsages;
};

// 하위 호환성을 위한 alias
export const getCoreAbilities = getCoreActions;
export const getAbilityById = getActionById;
export const getPlayerAbilities = getPlayerActions;
export const canUseAbility = canUseAction;
export const createAbilityUsage = createActionUsage;
export const processAbilityInteractions = processActionInteractions;
export const hasUsedAbilityInCurrentPhase = hasUsedActionInCurrentPhase;
export const getPlayerAbilityInCurrentPhase = getPlayerActionInCurrentPhase;

// 하위 호환성을 위한 type alias
export type CoreAbility = CoreAction;
export { ActionTargetType as AbilityTargetType };
export { ActionResultType as AbilityResultType };
